import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Compass, MapPin } from 'lucide-react';
import { CHEESERIES, ACCOMMODATIONS } from '../lib/data';
import { STATE_HUBS, REGION_HUBS } from '../lib/trailHubs';
import { imageUrl, type ImageKey } from '../lib/images';
import type { USState } from '../types';
import { cn } from '../lib/utils';

interface StateHubPageProps {
  state: USState;
}

/**
 * Per-state hero art. Intentionally uses existing Unsplash registry keys
 * so the page shares the project's thematic palette rather than introducing
 * new photography.
 */
const STATE_HERO: Partial<Record<USState, ImageKey>> = {
  VT: 'cow-mountain-pasture',
  ME: 'lighthouse-rocky-coast',
  NH: 'red-barn-large',
};

/**
 * Short editorial intro per state. These are the "chapter opener" paragraphs
 * the plan calls for — travel-led, not directory-led.
 */
const STATE_EDITORIAL: Partial<Record<USState, string[]>> = {
  VT: [
    'Vermont is the cheesemaking heart of New England. The combination of small-scale dairy farms, cave-aged natural-rind traditions, and Route 100 country inns turns a long weekend into a tasting route that threads through maple country, alpine passes, and working hill farms.',
    'The trail splits naturally into four driveable regions. Champlain Valley pairs Burlington food culture with lakeside farm stores. The Mad River Valley and Green Mountains host the classic Route 100 weekend. The Northeast Kingdom is the quiet pilgrimage — Jasper Hill, Willey\'s Store, Greensboro. Southern Vermont brings historic factories and sheep-milk wheels along the Connecticut River.',
  ],
  ME: [
    'Maine\'s cheese trail is built around the coast. Goat dairies, ricotta makers, and farmstead rounds stretch from the Portland waterfront up through the midcoast harbors and out to the edge of Acadia. A good Maine cheese weekend usually pairs a creamery morning with a harbor afternoon — the food culture is inseparable from the water.',
    'Three regions carry the trail. Greater Portland & Casco Bay anchors easy day trips from the Old Port. Midcoast Maine links Appleton, Rockland, and Camden through farmers markets and inland pasture roads. Acadia & Mount Desert Island is a compact detour — one very worthwhile farmstand stop folded into a Park Loop weekend.',
  ],
  NH: [
    'New Hampshire is the quieter middle of a Northern New England cheese trip. The state’s cheesemaking is smaller in scale than Vermont’s and less concentrated than Maine’s coastal goat culture — but it has something the other two don’t: a single historic cheese-and-pantry retail hub at Harman’s Cheese & Country Store in Sugar Hill, and a culture of self-serve honor-system farmstands that reward travellers who don’t mind arriving on their own schedule.',
    'Two regions carry the chapter today. The White Mountains arc links Harman’s with Sandwich Creamery’s self-serve farmstand and Robie Farm’s seventh-generation raw-milk store in the Connecticut River valley. The Merrimack Valley pairs biodynamic Brookford Farm in Canterbury with Contoocook Creamery’s 365-day self-service store at Bohanan Farm — a tight loop within an hour of Concord.',
  ],
};

/**
 * Practical travel notes that set expectations without turning into a FAQ.
 * Deliberately generic enough to apply to any Northeast cheese weekend.
 */
const PLANNING_NOTES: string[] = [
  'Call ahead. Farm stores keep short, seasonal hours and the smallest creameries are often by appointment only.',
  'Plan around production days. Watching curds get cut and stirred is half the experience.',
  'Pair your route with stays in one or two towns rather than hopping nightly — drives between regions add up fast.',
  'Buy a soft cooler. Many wheels travel home better than any souvenir.',
];

/**
 * Set document head metadata imperatively. Keeps this page dependency-free
 * (no react-helmet) while still emitting proper title / description / og /
 * canonical tags for each state hub route. Cleanup on unmount is not strictly
 * needed because every route sets its own, but we still reset on route change.
 */
function usePageMetadata({
  title,
  description,
  canonicalPath,
  ogImage,
}: {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
}) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const setMeta = (selector: string, attr: 'name' | 'property', attrValue: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', `https://curdcompass.com${canonicalPath}`);
    if (ogImage) setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://curdcompass.com${canonicalPath}`;

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, canonicalPath, ogImage]);
}

export default function StateHubPage({ state }: StateHubPageProps) {
  const hub = STATE_HUBS.find((h) => h.state === state);
  const regions = REGION_HUBS.filter((r) => r.state === state);
  const makers = CHEESERIES.filter((c) => c.state === state);
  const stays = ACCOMMODATIONS.filter((a) => a.state === state);
  const heroKey = STATE_HERO[state];
  const editorial = STATE_EDITORIAL[state] ?? [];

  const slug = hub?.slug ?? `${state.toLowerCase()}-cheese-trail`;

  usePageMetadata({
    title: hub ? `${hub.title} · The Curd Compass` : `${state} Cheese Trail · The Curd Compass`,
    description:
      hub?.subtitle ??
      'A curated cheese-trail chapter with creameries, farm stays, and weekend routes from The Curd Compass.',
    canonicalPath: `/${slug}`,
    ogImage: heroKey ? imageUrl(heroKey, { w: 1600 }) : undefined,
  });

  if (!hub) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-6">
        <div className="max-w-xl text-center space-y-6">
          <Compass className="mx-auto text-accent" size={48} />
          <h1 className="text-4xl font-serif text-ink">This chapter isn't open yet.</h1>
          <p className="text-ink-light">
            The Curd Compass only publishes a state hub once there's enough
            curated content to make the trip worth planning. Check back soon.
          </p>
          <Link to="/" className="btn-minimal inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to the trail
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      {/* Top bar */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center text-white">
              <Compass size={16} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-serif text-lg text-ink">The Curd Compass</span>
              <span className="text-[10px] uppercase tracking-widest text-ink-light">
                Cheese trails & tasting weekends
              </span>
            </div>
          </Link>
          <Link
            to="/"
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-light hover:text-ink transition-colors"
          >
            <ArrowLeft size={14} /> Back to the trail
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        {heroKey && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${imageUrl(heroKey, { w: 2000 })})` }}
            aria-hidden="true"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-ink/30 to-bg" aria-hidden="true" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl space-y-5"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/95 border border-border rounded-full text-[10px] font-bold uppercase tracking-widest text-ink">
              <MapPin size={12} className="text-accent" />
              {hub.eyebrow}
            </span>
            <h1 className="text-5xl md:text-7xl font-serif text-white tracking-tight leading-[1.02] drop-shadow-sm">
              {hub.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-2xl">
              {hub.subtitle}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {hub.bestFor.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1 bg-white/95 text-ink text-[10px] font-bold uppercase tracking-widest rounded-full"
                >
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-20">
        {/* Editorial intro */}
        {editorial.length > 0 && (
          <section className="max-w-3xl space-y-6">
            <span className="pill-minimal active">Field notes</span>
            <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
              Why this trail <span className="italic font-light text-accent">rewards a weekend.</span>
            </h2>
            <div className="space-y-4">
              {editorial.map((para, i) => (
                <p key={i} className="text-lg text-ink-light leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          </section>
        )}

        {/* Route regions */}
        {regions.length > 0 && (
          <section className="space-y-10">
            <div className="max-w-3xl space-y-4">
              <span className="pill-minimal active">Route regions</span>
              <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                Plan by <span className="italic font-light text-accent">driveable cluster.</span>
              </h2>
              <p className="text-base text-ink-light leading-relaxed">
                {hub.title.split(' Cheese')[0]} splits into {regions.length} travel regions.
                Pick one for a focused weekend, or stitch two adjacent regions
                into a longer loop.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {regions.map((region) => (
                <motion.article
                  key={region.key}
                  layout
                  className="bg-white/80 border border-border rounded-[28px] p-6 hover:shadow-sm transition-all"
                >
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                        Route region
                      </p>
                      <h3 className="text-3xl font-serif text-ink tracking-tight">
                        {region.shortTitle}
                      </h3>
                    </div>
                    <p className="text-sm text-ink-light leading-relaxed">{region.subtitle}</p>
                    <p className="font-serif italic text-lg text-ink-light leading-snug">
                      {region.mood}
                    </p>
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-ink-light font-bold">
                      <span>{region.cheeseIds.length} makers</span>
                      <span className="w-1 h-1 bg-border rounded-full" />
                      <span>{region.stayIds.length} stays</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>
        )}

        {/* Featured makers */}
        {makers.length > 0 && (
          <section className="space-y-10">
            <div className="max-w-3xl space-y-4">
              <span className="pill-minimal active">Featured makers</span>
              <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                {makers.length} creameries <span className="italic font-light text-accent">worth the drive.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {makers.slice(0, 6).map((maker) => (
                <article
                  key={maker.id}
                  className="card-minimal overflow-hidden bg-white"
                >
                  <div
                    className="aspect-[3/2] bg-cover bg-center"
                    style={{ backgroundImage: `url(${maker.image})` }}
                    aria-label={maker.name}
                  />
                  <div className="p-6 space-y-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                      {maker.city}, {maker.state}
                    </p>
                    <h3 className="text-xl font-serif text-ink tracking-tight">{maker.name}</h3>
                    <p className="text-sm text-ink-light leading-relaxed line-clamp-3">
                      {maker.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            {makers.length > 6 && (
              <Link
                to="/cheeses"
                className="btn-minimal inline-flex items-center gap-2"
              >
                View all {makers.length} makers
              </Link>
            )}
          </section>
        )}

        {/* Where to stay */}
        {stays.length > 0 && (
          <section className="space-y-10">
            <div className="max-w-3xl space-y-4">
              <span className="pill-minimal active">Where to stay</span>
              <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                Inns, farm stays, <span className="italic font-light text-accent">and long weekends.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {stays.slice(0, 6).map((stay) => (
                <article key={stay.id} className="card-minimal overflow-hidden bg-white">
                  <div
                    className="aspect-[3/2] bg-cover bg-center"
                    style={{ backgroundImage: `url(${stay.image})` }}
                    aria-label={stay.name}
                  />
                  <div className="p-6 space-y-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                      {stay.city}, {stay.state} &middot; {stay.style}
                    </p>
                    <h3 className="text-xl font-serif text-ink tracking-tight">{stay.name}</h3>
                    <p className="text-sm text-ink-light leading-relaxed line-clamp-3">
                      {stay.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            {stays.length > 6 && (
              <Link to="/stays" className="btn-minimal inline-flex items-center gap-2">
                View all {stays.length} stays
              </Link>
            )}
          </section>
        )}

        {/* Planning notes */}
        <section className="bg-sidebar/60 border border-border rounded-[28px] p-8 md:p-12">
          <div className="max-w-3xl space-y-6">
            <span className="pill-minimal active">Planning notes</span>
            <h2 className="text-3xl md:text-4xl font-serif text-ink tracking-tight leading-[1.15]">
              A few quiet rules for a <span className="italic font-light text-accent">better cheese weekend.</span>
            </h2>
            <ul className="space-y-3 text-[15px] text-ink-light leading-relaxed">
              {PLANNING_NOTES.map((note, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-accent font-bold shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA back to the app */}
        <section className="text-center space-y-6 py-8">
          <h2 className="text-3xl md:text-4xl font-serif text-ink tracking-tight">
            Ready to map a route?
          </h2>
          <p className="text-ink-light max-w-xl mx-auto">
            Open the interactive trail map to filter by region, milk type, or cheese style, and start stacking stops.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/" className="btn-minimal bg-ink text-white border-ink">
              Open the trail map
            </Link>
            <Link to="/stays" className="btn-minimal">
              Browse places to stay
            </Link>
          </div>
        </section>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-border bg-white/70">
        <div className={cn(
          'max-w-screen-2xl mx-auto px-6 md:px-12 py-6',
          'flex flex-col md:flex-row gap-3 md:items-center md:justify-between',
          'text-[11px] text-ink-light'
        )}>
          <p>© {new Date().getFullYear()} The Curd Compass. Cheese trails & tasting weekends.</p>
          <nav className="flex flex-wrap gap-4">
            <a href="/privacy.html" className="hover:text-ink">Privacy</a>
            <a href="/terms.html" className="hover:text-ink">Terms</a>
            <a href="/disclosure.html" className="hover:text-ink">Affiliate disclosure</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
